import lodashExtend from "lodash/extend";
import type React from "react";

import { HealthStateValue, type ViewComponent } from "~/models/dto";
import { theme } from "~/tools/twin-migration";

import { StackVizConfig } from "../stackviz/rendering/StackVizConfig";

import { Api } from "./openapi";

// merge via intersection types
export function extend<T, U>(first: T, second: U): T & U {
	return lodashExtend({}, second, first); // definitions in first object must override the same keys in second
}

export function stateToText(state: Api.HealthStateValue | HealthStateValue): string {
	// noinspection TypeScriptValidateTypes
	return String(state).toLowerCase().replaceAll("_", " ");
}

export function healthStateLabelStyle(healthState: HealthStateValue) {
	const color = healthStateToColor(healthState);

	return {
		backgroundColor: `#${color}`,
		borderColor: `#${color}`,
		color: `#${color}`,
	};
}

export const healthStateStyle = (ownState: Api.HealthStateValue | HealthStateValue): React.CSSProperties => {
	const color = healthStateToColor(ownState);

	switch (ownState) {
		case HealthStateValue.Uninitialized:
		case Api.HealthStateValue.UNINITIALIZED:
			return {
				border: `1px solid ${color}`,
			};
		default:
			return {
				backgroundColor: color,
			};
	}
};

export const healthStateStyleForComponent = (
	ownState: HealthStateValue | Api.HealthStateValue,
): React.CSSProperties => ({
	backgroundColor: `${healthStateToColorForComponents(ownState)}`,
});

const RELATION = "RELATION";
const COMPONENT = "COMPONENT";

type ElementType = typeof RELATION | typeof COMPONENT;

export const healthStateToColor = (healthState: Api.HealthStateValue | HealthStateValue): string => {
	switch (healthState) {
		case Api.HealthStateValue.CRITICAL:
		case HealthStateValue.Critical:
			return theme("colors.red.500");
		case HealthStateValue.Flapping:
		case HealthStateValue.Deviating:
			return theme("colors.orange.500");
		case HealthStateValue.Clear:
			return theme("colors.green.500");
		case Api.HealthStateValue.UNKNOWN:
		case HealthStateValue.Unknown:
			return theme("colors.graphite.500");
		case HealthStateValue.Disabled:
		default:
			return theme("colors.gray.400");
	}
};

export function healthStateToColorForText(healthState: Api.HealthStateValue | HealthStateValue): string {
	switch (healthState) {
		case HealthStateValue.Critical:
		case Api.HealthStateValue.CRITICAL:
			return theme("colors.red.500");
		case HealthStateValue.Flapping:
		case Api.HealthStateValue.FLAPPING:
		case HealthStateValue.Deviating:
		case Api.HealthStateValue.DEVIATING:
			return theme("colors.orange.600");
		case HealthStateValue.Clear:
		case Api.HealthStateValue.CLEAR:
			return theme("colors.green.700");
		default:
			return theme("colors.graphite.600");
	}
}

export const healthStateToColorForComponents = (healthState: HealthStateValue | Api.HealthStateValue): string => {
	switch (healthState) {
		case HealthStateValue.Critical:
		case Api.HealthStateValue.CRITICAL:
			return theme("colors.red.400");
		case HealthStateValue.Flapping:
		case Api.HealthStateValue.FLAPPING:
			return theme("colors.orange.400");
		case HealthStateValue.Deviating:
		case Api.HealthStateValue.DEVIATING:
			return theme("colors.orange.400");
		case HealthStateValue.Clear:
		case Api.HealthStateValue.CLEAR:
			return theme("colors.green.375");
		case HealthStateValue.Unknown:
		case Api.HealthStateValue.UNKNOWN:
			return theme("colors.graphite.275");
		case HealthStateValue.Disabled:
		case Api.HealthStateValue.DISABLED:
			return theme("colors.graphite.275");
		default:
			return theme("colors.graphite.275");
	}
};

export const healthStateToColorByElementTypeForViz = (type: ElementType) => {
	const colors = StackVizConfig.HEALTH_STATE_COLORS[type];
	return (healthState: HealthStateValue): number => {
		switch (healthState) {
			case HealthStateValue.Critical:
				return colors.CRITICAL;
			case HealthStateValue.Flapping:
				return colors.FLAPPING;
			case HealthStateValue.Deviating:
				return colors.DEVIATING;
			case HealthStateValue.Clear:
				return colors.CLEAR;
			case HealthStateValue.Unknown:
				return colors.UNKNOWN;
			case HealthStateValue.Disabled:
				return colors.DISABLED;
			default:
				return colors.UNKNOWN;
		}
	};
};

export const healthStateToColorForViz = healthStateToColorByElementTypeForViz(COMPONENT);
export const healthStateToColorForRelationsForViz = healthStateToColorByElementTypeForViz(RELATION);

/**
 * Check if health state is unhealthy
 * Atm all states except UNKNOWN and CLEAR are considered as unhealthy
 * @param healthState
 */
export const isHealthStateUnHealthy = (healthState: Api.HealthStateValue | HealthStateValue): boolean => {
	switch (healthState) {
		case HealthStateValue.Unknown:
		case Api.HealthStateValue.UNKNOWN:
		case HealthStateValue.Clear:
		case Api.HealthStateValue.CLEAR:
		case HealthStateValue.Disabled:
		case Api.HealthStateValue.DISABLED:
			return false;
		default:
			return true;
	}
};

export const getNumberOfFailingChecks = (component: ViewComponent): number =>
	component.failingChecks
		? component.failingChecks.filter((failingChecks) => isHealthStateUnHealthy(failingChecks.state.state)).length
		: 0;
